import {Col, Row} from 'antd';
import {
  ChargeDocument,
  PaymentMethods,
  TransactionTypes,
  useCreatePaymentMutation
} from 'api/generated';
import {PageHeader} from 'components';
import {Formik} from 'formik';
import {FormikHelpers} from 'formik/dist/types';
import {useMediaQuery} from 'hooks/useMediaQuery';
import {omitEmpty} from 'lib/omitEmpty';
import {amountToInt, arrayIncludesSome} from 'lib/utils';
import {validatePhone} from 'lib/validation';
import moment from 'moment-timezone';
import React, {useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import {useQueryParam} from 'use-query-params';
import * as yup from 'yup';
import {PAY_METHODS_FULL_INFO_REQUIRED, PayByLinkFormValues} from './common';
import {PayByLinkForm} from './PayByLinkForm';

const emptyAddress = {
  address: {
    city: '',
    country: '',
    line1: '',
    line2: '',
    zip: '',
    state: ''
  }
};

const validateExpireAt = (date?: Date) => {
  if (!date) return false;
  return moment(date).isAfter(moment());
};

const getValidationSchema = () => {
  const contactSchema = yup.object().shape({
    address: yup.object().shape({
      city: yup.string().required('city is required'),
      country: yup.string().required('country is required'),
      line1: yup.string().required('address is required'),
      zip: yup.string().required('zip is required'),
      state: yup.string().required('state is required')
    })
  });
  const customerSchema = yup.object().shape({
    email: yup.string().email(),
    name: yup.string().matches(/^[A-Za-zÀ-ú0-9 .]*$/, 'no special characters are allowed'),
    phone: yup.string().test('phone', 'invalid phone format', validatePhone)
  });
  return yup.object().shape({
    amount: yup.number().defined().required().moreThan(0),
    orderId: yup
      .string()
      .matches(
        /^[a-zA-Z0-9_@./#&+-]{1,40}$/,
        'please use alphanumeric characters, no special characters are allowed'
      ),
    description: yup
      .string()
      .matches(/^[A-Za-zÀ-ú0-9 .,&_@/#+-]*$/, 'no special characters are allowed'),
    allowedPaymentMethods: yup.array().of(yup.string()).min(1).required(),
    expireAt: yup
      .date()
      .test('in_future', 'expiration date should be in the future', validateExpireAt),
    customer: customerSchema.when('allowedPaymentMethods', {
      is: (values: PaymentMethods[]) => arrayIncludesSome(values, PAY_METHODS_FULL_INFO_REQUIRED),
      then: (schema) => schema.shape({email: yup.string().email().required()})
    }),
    transactionType: yup.string().oneOf([TransactionTypes.AUTH, TransactionTypes.SALE]),
    billingDetails: yup.object().when('hasBillingDetails', {
      is: true,
      then: contactSchema.required(),
      otherwise: yup.object().optional()
    }),
    shippingDetails: yup.object().when('hasShippingDetails', {
      is: true,
      then: contactSchema.required(),
      otherwise: yup.object().optional()
    })
  });
};

export const PayByLink: React.FC = () => {
  const history = useHistory();
  const [accountId] = useQueryParam('accountId');
  const {isTablet} = useMediaQuery();

  const [createPayment] = useCreatePaymentMutation();
  const validationSchema = useMemo(() => getValidationSchema(), []);

  const initialValues: PayByLinkFormValues = {
    accountId: accountId as string,
    amount: 0,
    manualCapture: false,
    hasBillingDetails: false,
    hasShippingDetails: false,
    allowedPaymentMethods: [],
    expireAt: moment().add(7, 'days'),
    customer: {email: ''},
    billingDetails: {...emptyAddress},
    shippingDetails: {...emptyAddress}
  };

  const handleSubmit = async (
    {
      amount,
      manualCapture,
      expireAt,
      allowedPaymentMethods,
      billingDetails,
      shippingDetails,
      hasShippingDetails,
      hasBillingDetails,
      ...input
    }: PayByLinkFormValues,
    {setSubmitting}: FormikHelpers<PayByLinkFormValues>
  ) => {
    let transactionType = TransactionTypes.SALE;
    if (manualCapture && !allowedPaymentMethods?.includes(PaymentMethods.BIZUM)) {
      transactionType = TransactionTypes.AUTH;
    }
    const {data} = await createPayment({
      variables: {
        input: omitEmpty({
          ...input,
          accountId: accountId as string,
          billingDetails: hasBillingDetails ? billingDetails : null,
          shippingDetails: hasShippingDetails ? shippingDetails : null,
          expireAt: expireAt ? moment(expireAt).unix() : null,
          allowedPaymentMethods,
          transactionType,
          amount: amountToInt(amount)
        }) as any
      },
      update: (cache, {data}) => {
        const payment = data?.createPayment;
        if (payment) {
          const {nextAction, __typename, ...charge} = payment;
          cache.writeQuery({
            query: ChargeDocument,
            variables: {id: payment.id},
            data: {
              charge: {
                ...charge,
                __typename: 'Charge',
                providerReferenceId: null,
                providerId: null,
                pointOfSaleId: null,
                subscriptionId: null,
                providerInternalId: null,
                acquirer: null,
                providerStatusCode: null,
                checkoutId: null,
                processor: null,
                providerStatusMessage: null,
                issuerStatusCode: null,
                issuerStatusMessage: null,
                billingPlan: null,
                descriptor: null
              }
            }
          });
        }
      }
    });
    setSubmitting(false);
    const id = data?.createPayment?.id;
    if (id) {
      history.push(`/payments/${id}`, {action: 'sendPaymentLink'});
    }
  };

  return (
    <Row>
      <Col span={isTablet ? 24 : 12} offset={isTablet ? 0 : 6}>
        <PageHeader title="Create payment" />
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          validateOnBlur={true}
          validateOnChange={false}
          onSubmit={handleSubmit}>
          <PayByLinkForm />
        </Formik>
      </Col>
    </Row>
  );
};
